import React from 'react';
import { SubNavCard } from './SubNavCard';
import { SubNavSheet } from './SubNavSheet';
import { SubNavAccordion } from './SubNavAccordion';
import type { SubNavProps } from './types';
import { useSubNav } from './hooks/useSubNav';

// Re-export types for backward compatibility
export type { SubNavItem, SubNavGroup, SubNavProps } from './types';

// --- MAIN COMPONENT ---

const SubNav = React.forwardRef<HTMLDivElement, SubNavProps>(
  ({ 
    navigationData, 
    activeItem, 
    activeTab, 
    onItemClick, 
    variant = 'auto',
    side = 'left', 
    showActivities = false,
    recentActivities = [],
    activityDetails,
    onViewAllActivities,
    onOpenRecord,
    onClose,
    isOpen = true,
    linkComponent,
    className, 
    ...props 
  }, ref) => {
    const { currentVariant, currentSide, openForSheet } = useSubNav({
      variant,
      side,
      isOpen,
      onClose,
    });

    if (!navigationData || navigationData.length === 0) {
      return null;
    }

    // Render the appropriate variant
    switch (currentVariant) {
      case 'sheet':
        return (
          <SubNavSheet
            ref={ref}
            navigationData={navigationData}
            activeItem={activeItem}
            onItemClick={onItemClick}
            side={currentSide}
            showActivities={showActivities}
            recentActivities={recentActivities}
            activityDetails={activityDetails}
            onViewAllActivities={onViewAllActivities}
            onOpenRecord={onOpenRecord}
            onClose={onClose}
            isOpen={openForSheet}
            activeTab={activeTab}
            linkComponent={linkComponent}
            className={className}
            {...props}
          />
        );
      case 'accordion':
        return (
          <SubNavAccordion
            ref={ref}
            navigationData={navigationData}
            activeItem={activeItem}
            activeTab={activeTab}
            onItemClick={onItemClick}
            linkComponent={linkComponent}
            className={className}
            {...props}
          />
        );
      default:
        return (
          <SubNavCard
            ref={ref}
            navigationData={navigationData}
            activeItem={activeItem}
            activeTab={activeTab}
            onItemClick={onItemClick}
            showActivities={showActivities}
            recentActivities={recentActivities}
            activityDetails={activityDetails}
            onViewAllActivities={onViewAllActivities}
            onOpenRecord={onOpenRecord}
            onClose={onClose}
            linkComponent={linkComponent}
            className={className}
            {...props}
          />
        );
    }
  }
);

SubNav.displayName = 'SubNav';

export default SubNav;
